import React from 'react'
import { toast } from 'sonner'
import styled from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import { useDispatch, useSelector } from 'react-redux'
import ExampleUserAvatarUrl from 'src/assets/avatar-placeholder.svg'
import MUIChatBubble from '@material-ui/icons/ChatBubbleOutline'
import {
  setConversationOpenedRoutine,
  sendMessageRoutine
} from 'src/features/messages/duck/actions'
import ConversationInput from 'src/features/messages/components/ConversationInput'
import { selectCurrentUser } from 'src/features/account/duck/selectors'
import NotificationCoordinatorService from 'src/services/NotificationCoordinatorService'
import DesktopNotificationService from 'src/services/DesktopNotificationService'
import { store } from 'src/ducks/storeProvider'

const ChatMessageNotificationToast = ({ message, onClose, onClick }) => {
  const intl = useIntl()
  const currentUser = useSelector(selectCurrentUser)
  const dispatch = useDispatch()

  const getSenderInfo = () => {
    if (!message.sender) return null

    return {
      name: `${message.sender.first_name} ${message.sender.last_name}`,
      avatar: message.sender.avatar?.url || ExampleUserAvatarUrl,
      companyName: message.sender.company.name || ''
    }
  }

  const getContextInfo = () => {
    const context = message.message_data?.conversation?.context
    if (context) {
      return {
        title:
          context.reference_number ||
          intl.formatMessage({ id: 'messages.offerChat' }),
        subtitle: ''
      }
    }
    return {
      title: intl.formatMessage({ id: 'messages.personalChat' }),
      subtitle: ''
    }
  }

  const handleQuickReply = async replyMessage => {
    try {
      const participants =
        message.message_data?.conversation?.participants || []
      const interlocutor = participants.find(p => p.id !== currentUser.id)

      if (!interlocutor) {
        console.error('No interlocutor found')
        return false
      }

      const messageData = {
        conversationId: message.conversation_id,
        receiverId: interlocutor.id,
        message: replyMessage
      }

      if (message.message_data?.conversation?.context_id) {
        messageData.contextId = message.message_data.conversation.context_id
        messageData.contextType = message.message_data.conversation.context_type
      }

      return await new Promise(resolve =>
        dispatch(
          sendMessageRoutine.trigger({
            ...messageData,
            chatId: String(message.conversation_id),
            callback: () => {
              toast.dismiss()
              resolve(true)
            },
            onError: () => {
              toast.error(intl.formatMessage({ id: 'messages.sendFailed' }))
              resolve(false)
            }
          })
        )
      )
    } catch (e) {
      console.error(e)
      toast.error(intl.formatMessage({ id: 'messages.sendFailed' }))
      return false
    }
  }

  const senderInfo = getSenderInfo()
  const contextInfo = getContextInfo()

  if (!senderInfo) return null

  return (
    <ToastWrapper onClick={onClick}>
      <CloseButton
        onClick={e => {
          e.stopPropagation()
          onClose()
        }}
        title={intl.formatMessage({ id: 'notifications.closeNotification' })}
      />
      <MessageContainer>
        <Header>
          <ChatIcon />
          <HeaderText>
            {intl.formatMessage({ id: 'messages.newMessageReceived' })}
          </HeaderText>
        </Header>
        <Content>
          <Avatar src={senderInfo.avatar} />
          <MessageInfo>
            <SenderName>
              {senderInfo.name}
              {senderInfo.companyName && (
                <CompanyId>{senderInfo.companyName}</CompanyId>
              )}
            </SenderName>
            <ContextInfo>
              <ContextTitle>{contextInfo.title}</ContextTitle>
              {contextInfo.subtitle && (
                <ContextSubtitle>{contextInfo.subtitle}</ContextSubtitle>
              )}
              {message.messageData?.conversation?.context?.referenceNumber && (
                <ReferenceNumber>
                  {message.messageData.conversation.context.referenceNumber}
                </ReferenceNumber>
              )}
            </ContextInfo>
            <MessageWrapper>
              <MessageText>{message.message}</MessageText>
            </MessageWrapper>
          </MessageInfo>
        </Content>
        <QuickReplySection
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <StyledConversationInput
            placeholder={intl.formatMessage({
              id: 'messages.quickReplyPlaceholder',
              defaultMessage: 'Szybka odpowiedź...'
            })}
            onSubmit={handleQuickReply}
          />
        </QuickReplySection>
      </MessageContainer>
    </ToastWrapper>
  )
}

export const showChatMessageNotificationToast = async message => {
  if (message.is_conversation_muted) {
    return
  }

  const notificationCoordinator = NotificationCoordinatorService.getInstance()
  // determine if the conversation for this message is currently open (not minimized)
  // use store to inspect chat sessions and openChatIds
  const state = store.getState()
  const sessions = state.messages?.chatSessions || {}
  const openChatIds = state.messages?.openChatIds || []
  const sessionEntry = Object.entries(sessions).find(
    ([, s]) => String(s?.conversationId) === String(message.conversation_id)
  )
  const sessionIdForMessage = sessionEntry ? sessionEntry[0] : null
  const isCurrentConversationOpen =
    sessionIdForMessage &&
    openChatIds.includes(sessionIdForMessage) &&
    state.messages.activeChatId === sessionIdForMessage &&
    !document.hidden &&
    document.hasFocus() &&
    sessions[sessionIdForMessage].scrollPosition?.nearBottom !== false

  try {
    const decision = await notificationCoordinator.shouldShowNotification()

    if (decision.showToast && !isCurrentConversationOpen) {
      const toastId = toast.custom(
        () => {
          const ToastWithDispatch = () => {
            const dispatch = useDispatch()
            const currentUser = useSelector(selectCurrentUser)

            return (
              <ChatMessageNotificationToast
                message={message}
                onClose={() => toast.dismiss(toastId)}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (
                    e.target.tagName !== 'INPUT' &&
                    !e.target.closest('form')
                  ) {
                    toast.dismiss(toastId)

                    const conversationData = {
                      conversationOpened: true,
                      conversationId: message.conversation_id
                    }

                    if (message.message_data?.conversation?.context) {
                      conversationData.contextId =
                        message.message_data.conversation.context_id
                      conversationData.contextType =
                        message.message_data.conversation.context_type
                    }

                    const participants =
                      message.message_data?.conversation?.participants || []
                    const recipient = participants.find(
                      p => p.id !== currentUser.id
                    )

                    if (recipient) {
                      conversationData.receiverId = recipient.id
                    }
                    dispatch(setConversationOpenedRoutine(conversationData))
                  }
                }}
              />
            )
          }

          return <ToastWithDispatch />
        },
        {
          style: {
            background: 'white',
            border: '1px solid #e2e8f0',
            padding: 0,
            width: '400px',
            height: 'auto',
            minHeight: '160px',
            maxHeight: '300px'
          },
          duration: 6000,
          dismissible: true
        }
      )

      notificationCoordinator.markNotificationShown()
      return { toastId }
    }

    if (decision.showDesktop) {
      const desktopNotificationService = DesktopNotificationService.getInstance()

      if (desktopNotificationService.hasPermission()) {
        try {
          const notificationData = message.desktop_notification

          const conversationData = {
            conversationId: notificationData.data.conversation_id,
            contextId: notificationData.data.context_id,
            contextType: notificationData.data.context_type,
            receiverId: notificationData.data.sender_id,
            conversationOpened: true
          }

          desktopNotificationService.show({
            title: notificationData.title,
            body: notificationData.body,
            icon: notificationData.icon,
            image: notificationData.image,
            badge: notificationData.badge,
            tag: notificationData.tag,
            requireInteraction: notificationData.requireInteraction || false,
            silent: notificationData.silent || true,
            dir: notificationData.dir || 'auto',
            lang: notificationData.lang || 'pl',
            vibrate: notificationData.vibrate,
            actions: notificationData.actions,
            data: notificationData.data,
            autoClose: 5000,
            onClick: () => {
              window.focus()
              store.dispatch(setConversationOpenedRoutine(conversationData))
            },
            onError: error => {
              console.error('Desktop notification error:', error)
            }
          })

          notificationCoordinator.markNotificationShown()
          return { desktop: true }
        } catch (error) {
          console.error('Desktop notification error:', error)
        }
      }
    }
  } catch (error) {
    console.error('Notification coordination error:', error)
  }
}

const ToastWrapper = styled.div`
  position: relative;
  width: 400px;
  min-height: 160px;
  max-height: 300px;
  pointer-events: auto;
  overflow: hidden;
`

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.black.alpha(0.6).hex()};
  font-size: 16px;
  line-height: 1;
  z-index: 10;

  &:hover {
    background-color: ${({ theme }) => theme.colors.ghost.alpha(0.3).hex()};
    color: ${({ theme }) => theme.colors.black.hex()};
  }

  &:before {
    content: '×';
    font-weight: bold;
    margin-bottom: 4px;
  }
`

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  cursor: pointer;
  padding: 12px 16px;
  box-sizing: border-box;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const ChatIcon = styled(MUIChatBubble)`
  font-size: 16px !important;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  margin-right: 6px;
`

const HeaderText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  text-transform: uppercase;
`

const Content = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  margin-bottom: 8px;
  overflow: hidden;
`

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`

const MessageInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  justify-content: flex-start;
  overflow: hidden;
`

const SenderName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black.hex()};
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
`

const CompanyId = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.black.alpha(0.5).hex()};
`

const ContextInfo = styled.div`
  margin-bottom: 4px;
`

const ContextTitle = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  display: block;
`

const ContextSubtitle = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black.alpha(0.6).hex()};
  display: block;
`

const ReferenceNumber = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  display: block;
  text-transform: uppercase;
`

const MessageWrapper = styled.div`
  position: relative;
  max-height: 120px;
  overflow: hidden;
`

const MessageText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black.alpha(0.8).hex()};
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  display: -webkit-box;
  -webkit-line-clamp: 9;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledConversationInput = styled(ConversationInput)`
  input {
    font-weight: 400 !important;
  }
`

const QuickReplySection = styled.div`
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
  cursor: default;
  flex-shrink: 0;
`

export default ChatMessageNotificationToast
